import React, { createContext, useContext, useState, useEffect } from 'react';
import { getCurrentUser, login as apiLogin, logout as apiLogout, getMyPermissions, getMyModules, impersonateUser as apiImpersonate, stopImpersonation as apiStopImpersonation, onBillingRequired } from '../api';

// Billing for the signed-in workspace, from /auth/me. An older server (or
// one without billing switched on) sends none, which means not billed.
const NO_BILLING = { applies: false, locked: false };
export const normalizeBilling = (billing) =>
  (billing && typeof billing === 'object' ? { ...NO_BILLING, ...billing, locked: !!billing.locked } : NO_BILLING);

// /auth/me may put billing beside the user or on it; read either.
const billingFrom = (data) => normalizeBilling(data?.billing ?? data?.user?.billing);
const withBilling = (u, billing) => (u ? { ...u, billing } : u);

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState({});
  const [visibleModules, setVisibleModules] = useState([]);
  const [modulesLoaded, setModulesLoaded] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [originalUser, setOriginalUser] = useState(null);
  // Multi-tenant: the platform owner (signed in to the original workspace)
  // manages customer workspaces. Customer admins never see that section.
  const [isPlatformOwner, setIsPlatformOwner] = useState(false);
  const [workspace, setWorkspace] = useState(null);
  // Email: whether this person has confirmed their address, and whether the
  // server can deliver mail at all (false means it only logs messages).
  // null until /auth/me has said, so nothing shows for an older server.
  const [emailVerified, setEmailVerified] = useState(null);
  const [emailEnabled, setEmailEnabled] = useState(null);
  // Billing: what /auth/me said, plus whether any call has since come back
  // 402 billingRequired. Either one locks the workspace.
  const [billing, setBilling] = useState(NO_BILLING);
  const [billingRequired, setBillingRequired] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (typeof onBillingRequired !== 'function') return undefined;
    return onBillingRequired(() => setBillingRequired(true));
  }, []);

  const applyBilling = (data) => {
    const next = billingFrom(data);
    setBilling(next);
    return next;
  };

  const fetchVisibleModules = async () => {
    try {
      const response = await getMyModules();
      setVisibleModules(response.data || []);
      setModulesLoaded(true);
    } catch (error) {
      console.error('Failed to fetch visible modules:', error);
      setVisibleModules([]);
      setModulesLoaded(true);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await getMyPermissions();
      // API returns an array of { entity, view_level, edit_level, create_allowed, delete_level }
      // Convert to object keyed by entity for easy lookup
      const permsArray = response.data || [];
      const permsObj = {};
      if (Array.isArray(permsArray)) {
        permsArray.forEach(p => {
          permsObj[p.entity] = p;
        });
      }
      setPermissions(permsObj);
    } catch (error) {
      setPermissions({});
    }
  };

  const applyEmailInfo = (data) => {
    setEmailVerified(typeof data?.emailVerified === 'boolean' ? data.emailVerified : null);
    setEmailEnabled(typeof data?.emailEnabled === 'boolean' ? data.emailEnabled : null);
  };

  // The login response may not carry the workspace and email fields; /auth/me does.
  const fetchWorkspaceInfo = async (loginData) => {
    if (loginData && 'isPlatformOwner' in loginData && 'emailVerified' in loginData && ('billing' in loginData || 'billing' in (loginData.user || {}))) {
      setIsPlatformOwner(!!loginData.isPlatformOwner);
      setWorkspace(loginData.workspace || null);
      applyEmailInfo(loginData);
      const b = applyBilling(loginData);
      setUser(u => withBilling(u, b));
      return;
    }
    try {
      const response = await getCurrentUser();
      setIsPlatformOwner(!!response.data.isPlatformOwner);
      setWorkspace(response.data.workspace || null);
      applyEmailInfo(response.data);
      const b = applyBilling(response.data);
      setUser(u => withBilling(u, b));
    } catch (error) {
      setIsPlatformOwner(false);
      setWorkspace(null);
      applyEmailInfo(null);
    }
  };

  const checkAuth = async () => {
    setBillingRequired(false);
    try {
      const response = await getCurrentUser();
      const b = applyBilling(response.data);
      setUser(withBilling(response.data.user, b));
      setMustChangePassword(!!response.data.mustChangePassword);
      setIsImpersonating(!!response.data.isImpersonating);
      setOriginalUser(response.data.originalUser || null);
      setIsPlatformOwner(!!response.data.isPlatformOwner);
      setWorkspace(response.data.workspace || null);
      applyEmailInfo(response.data);
      await Promise.all([fetchPermissions(), fetchVisibleModules()]);
    } catch (error) {
      setUser(null);
      setMustChangePassword(false);
      setIsPlatformOwner(false);
      setWorkspace(null);
      applyEmailInfo(null);
      setBilling(NO_BILLING);
      setPermissions({});
      setVisibleModules([]);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await apiLogin(email, password);
    setBillingRequired(false);
    setUser(withBilling(response.data.user, billingFrom(response.data)));
    setMustChangePassword(!!response.data.mustChangePassword);
    await Promise.all([fetchPermissions(), fetchVisibleModules(), fetchWorkspaceInfo(response.data)]);
    return response.data;
  };

  const logout = async () => {
    await apiLogout();
    setUser(null);
    setMustChangePassword(false);
    setPermissions({});
    setVisibleModules([]);
    setModulesLoaded(false);
    setIsImpersonating(false);
    setOriginalUser(null);
    setIsPlatformOwner(false);
    setWorkspace(null);
    applyEmailInfo(null);
    setBilling(NO_BILLING);
    setBillingRequired(false);
  };

  const startImpersonation = async (userId) => {
    await apiImpersonate(userId);
    // Re-fetch everything as the impersonated user
    await checkAuth();
  };

  const stopImpersonating = async () => {
    await apiStopImpersonation();
    // Re-fetch everything as the admin
    await checkAuth();
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    return !!user[permission];
  };

  const canDo = (entity, action) => {
    if (!user) return false;
    // Admin users can do everything
    if (user.role_name === 'Admin') return true;
    const perm = permissions[entity];
    if (!perm) return false;
    if (action === 'create') return !!perm.create_allowed;
    if (action === 'view') return perm.view_level !== 'none';
    if (action === 'edit') return perm.edit_level !== 'none';
    if (action === 'delete') return perm.delete_level !== 'none';
    return false;
  };

  const canOverridePrice = () => {
    if (!user) return false;
    if (user.role_name === 'Admin') return true;
    if (permissions.opportunities?.price_override) return true;
    if (permissions.products?.edit_level === 'all') return true;
    return false;
  };

  const getMaxDiscountPct = () => {
    if (!user) return 0;
    if (user.role_name === 'Admin') return 100;
    return permissions.opportunities?.max_discount_pct ?? 10;
  };

  const isModuleVisible = (moduleKey) => {
    if (!user) return false;
    // Admin sees everything
    if (user.role_name === 'Admin') return true;
    // Secure default: hide everything until modules have loaded
    if (!modulesLoaded) return false;
    // If loaded and empty, server explicitly said "show all"
    if (visibleModules.length === 0) return true;
    // Check if module is in the visible modules list
    return visibleModules.some(m => m.module_key === moduleKey);
  };

  const value = {
    user,
    loading,
    login,
    logout,
    hasPermission,
    canDo,
    permissions,
    checkAuth,
    visibleModules,
    isModuleVisible,
    refreshModules: fetchVisibleModules,
    modulesLoaded,
    mustChangePassword,
    setMustChangePassword,
    isImpersonating,
    originalUser,
    startImpersonation,
    stopImpersonating,
    canOverridePrice,
    getMaxDiscountPct,
    isPlatformOwner,
    workspace,
    emailVerified,
    emailEnabled,
    setEmailVerified,
    billing,
    // True when the workspace is locked for billing: every page but sign-in
    // and billing is replaced by the locked screen.
    billingLocked: !!user && (billing.locked || billingRequired),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
